import { Outlet, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import { useEffect, useRef, useState } from "react";
import { Loader } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import UpgradeModal from "../UpgradeModal/UpgradeModal";
import { UPGRADE_MODAL_URL, registerUpgradeModalOpener } from "../../lib/upgradeModal";

// Former checkout pages: existing links and access-expired redirects now open the upgrade modal.
const UPGRADE_ALIAS_PATHS = ["/access-expired", "/upgrade"];

const PrivateRoutesWrapper = () => {
  const { user, accessStatus, trial, validateCookie } = useUser();
  const [loading, setLoading] = useState<boolean>(true);
  const [authorised, setAuthorised] = useState<boolean>(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(false);
  const hasShownTrialEndedModal = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isStudent = user?.type === "student";
  const trialEnded = isStudent && accessStatus === "expired" && trial !== null;

  useEffect(() => {
    registerUpgradeModalOpener(() => setShowUpgradeModal(true));
    return () => registerUpgradeModalOpener(null);
  }, []);

  // Offer the upgrade once when the trial is found to have ended; after closing it the user can review their answers.
  useEffect(() => {
    if (trialEnded && !hasShownTrialEndedModal.current) {
      hasShownTrialEndedModal.current = true;
      setShowUpgradeModal(true);
    }
  }, [trialEnded]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("upgrade") !== "1") {
      return;
    }
    setShowUpgradeModal(true);
    params.delete("upgrade");
    const search = params.toString();
    navigate(`${location.pathname}${search ? `?${search}` : ""}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    const callValidateCookieFunction = async () => {
      try {
        const result = await validateCookie();
        setAuthorised(result.valid);
        setLoading(false);
      } catch (err) {
        console.error(err);
      }
    };

    // only check cookie if no user obj exists in state already.
    if (user) {
      setLoading(false);
      setAuthorised(true);
    } else {
      callValidateCookieFunction();
    }
  }, [user, validateCookie]);

  if (loading) {
    return (
      <div
        style={{
          display: "grid",
          height: "100%",
        }}
      >
        <Loader
          size="md"
          content="Loading..."
          style={{
            margin: "auto",
          }}
        />
      </div>
    );
  }

  if (!authorised) {
    return <Navigate to="/login" />;
  }

  // An ended trial can still review its own answers: the dashboard, the trialled subject and the review page.
  const allowedPathsWhenExpired = trialEnded && trial
    ? ["/dashboard", `/subjects/${trial.subjectId}`, "/review"]
    : ["/dashboard"];
  const redirectToUpgradeModal =
    UPGRADE_ALIAS_PATHS.includes(location.pathname) ||
    (isStudent && accessStatus === "expired" && !allowedPathsWhenExpired.includes(location.pathname));

  // The modal keeps its place in the tree through these redirects so its warmed-up checkout survives them.
  return (
    <>
      {redirectToUpgradeModal ? <Navigate to={UPGRADE_MODAL_URL} replace /> : <Outlet />}
      <UpgradeModal show={showUpgradeModal} onHide={() => setShowUpgradeModal(false)} />
    </>
  );
};

export default PrivateRoutesWrapper;
